import { adapterFactory } from "angular-calendar/date-adapters/date-fns";
import { CalendarModule, DateAdapter } from "angular-calendar";
import { CdkTableModule } from "@angular/cdk/table";
import {
  CommonModule as NgCommonModule,
  NgOptimizedImage,
} from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { GoogleMapsModule } from "@angular/google-maps";
import { MatTreeModule } from "@angular/material/tree";
import { RouterModule, Routes } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { NgxUploaderModule } from "@angular-ex/uploader";
import { LandingPageProviderModule } from "../../features/landing-page/landing-page.provider.module";
import {
  createDefaultIntervalRunner,
  INTERVAL_RUNNER_FACTORY,
} from "../../features/shared/interval-runner/interval-runner";
import { DoughnutChartWithLegendComponent } from "../../shared/components/doughnut-chart-with-legend/doughnut-chart-with-legend.component";
import { SelectorOptionsService } from "../../shared/components/filtering/selector-options.service";
import { LoaderComponent } from "../../shared/components/loader/loader.component";
import { SharedComponentsModule } from "../../shared/components/shared-components.module";
import { PublicationService } from "../../shared/content/data/publication.service";
import { PublicationProviderModule } from "../../shared/content/domain/publication.provider.module";
import { BetaCampaignGuard } from "../../shared/guards/beta-campaign.guard";
import { AngularMaterialModule } from "../../shared/material/material.module";
import { SharedPipesModule } from "../../shared/pipes/shared-pipes.module";
import { BrandCampaignResolverGuard } from "../shared/brand-campaign-resolver.guard";
import { BrandCampaignSubmitCheckDialogComponent } from "./brand-campaign-submit-check-dialog/brand-campaign-submit-check-dialog.component";
import { BrandPublicationsHeaderComponent } from "./content/components/brand-publications-header/brand-publications-header.component";
import { BrandContentComponent } from "./content/pages/brand-content/brand-content.component";
import { BrandPartnerBudgetService } from "./shared/services/brand-partner-budget.service";
import { BrandCampaignAdDialogComponent } from "./brand-campaign-ad-dialog/brand-campaign-ad-dialog.component";
import { BrandCampaignContentCalendarPostEditDialogComponent } from "./brand-campaign-content-calendar/brand-campaign-content-calendar-post-edit-dialog/brand-campaign-content-calendar-post-edit-dialog.component";
import { BrandCampaignContentCalendarPostSelectionDialogComponent } from "./brand-campaign-content-calendar/brand-campaign-content-calendar-post-selection-dialog/brand-campaign-content-calendar-post-selection-dialog.component";
import {
  BrandCampaignContentCalendarPostsCarouselComponent,
  BrandCampaignContentCalendarPostsCarouselItemDirective,
  BrandCampaignContentCalendarPostsCarouselItemElementDirective,
} from "./brand-campaign-content-calendar/brand-campaign-content-calendar-post-selection-dialog/brand-campaign-content-calendar-posts-carousel/brand-campaign-content-calendar-posts-carousel.component";
import { BrandCampaignContentCalendarComponent } from "./brand-campaign-content-calendar/brand-campaign-content-calendar.component";
import { BrandCampaignContentFormFbLandingPageUrlComponent } from "./brand-campaign-content/brand-campaign-content-form-fb-landing-page-url/brand-campaign-content-form-fb-landing-page-url.component";
import { BrandCampaignContentFormPostSuggestedTextComponent } from "./brand-campaign-content/brand-campaign-content-form-post-text/brand-campaign-content-form-post-suggested-text.component";
import { BrandCampaignContentComponent } from "./brand-campaign-content/brand-campaign-content.component";
import { BrandCampaignCategoryMoveDialogComponent } from "./brand-campaign-content/brand-campaign-move-post-category-dialog/brand-campaign-move-post-category-dialog.component";
import { CampaignAddContentDialogComponent } from "./brand-campaign-content/campaign-add-content-dialog/campaign-add-content-dialog.component";
import { CampaignCategoryFormDialogComponent } from "./brand-campaign-content/campaign-category-form-dialog/campaign-category-form-dialog.component";
import { CampaignContentFormBannerDialogComponent } from "./brand-campaign-content/campaign-content-form-banner-dialog/campaign-content-form-banner-dialog.component";
import { CampaignContentFormGoogleAdDialogComponent } from "./brand-campaign-content/campaign-content-form-google-ad-dialog/campaign-content-form-google-ad-dialog.component";
import { CampaignContentFormLinkPostDialogComponent } from "./brand-campaign-content/campaign-content-form-link-post-dialog/campaign-content-form-link-post-dialog.component";
import { CampaignContentFormPostCarouselDialogComponent } from "./brand-campaign-content/campaign-content-form-post-carousel-dialog/campaign-content-form-post-carousel-dialog.component";
import {
  CarouselEditItemDirective,
  CarouselEditItemElementDirective,
} from "./brand-campaign-content/campaign-content-form-post-carousel-dialog/carousel-edit/carousel-edit-item.directive";
import { CarouselEditComponent } from "./brand-campaign-content/campaign-content-form-post-carousel-dialog/carousel-edit/carousel-edit.component";
import { CarouselPreviewComponent } from "./brand-campaign-content/campaign-content-form-post-carousel-dialog/carousel-preview/carousel-preview.component";
import { CampaignContentFormPostImageDialogComponent } from "./brand-campaign-content/campaign-content-form-post-image-dialog/campaign-content-form-post-image-dialog.component";
import { CampaignContentFormPostVideoDialogComponent } from "./brand-campaign-content/campaign-content-form-post-video-dialog/campaign-content-form-post-video-dialog.component";
import { CampaignContentFormVideoDialogComponent } from "./brand-campaign-content/campaign-content-form-video-dialog/campaign-content-form-video-dialog.component";
import { CampaignContentPostCommentsDialogComponent } from "./brand-campaign-content/campaign-content-post/campaign-content-post-comments-dialog/campaign-content-post-comments-dialog.component";
import { CampaignContentPostComponent } from "./brand-campaign-content/campaign-content-post/campaign-content-post.component";
import { BrandCampaignDashboardComponent } from "./brand-campaign-dashboard/brand-campaign-dashboard.component";
import { BrandCampaignHeaderComponent } from "./brand-campaign-header/brand-campaign-header.component";
import { BrandCampaignLandingPageComponent } from "./brand-campaign-landing-page/brand-campaign-landing-page.component";
import { BrandCampaignMapComponent } from "./brand-campaign-map/brand-campaign-map.component";
import { BrandCampaignNameEditDialogComponent } from "./brand-campaign-name-edit-dialog/brand-campaign-name-edit-dialog.component";
import { BrandCampaignNavigationComponent } from "./brand-campaign-navigation/brand-campaign-navigation.component";
import { BrandCampaignPartnerEditDialogComponent } from "./brand-campaign-partner-edit-dialog/brand-campaign-partner-edit-dialog.component";
import { BrandCampaignPartnerStatusChartDialogComponent } from "./brand-campaign-partners/brand-campaign-partner-status-chart-dialog/brand-campaign-partner-status-chart-dialog.component";
import { BrandCampaignPartnersComponent } from "./brand-campaign-partners/brand-campaign-partners.component";
import { BrandCampaignPosComponent } from "./brand-campaign-pos/brand-campaign-pos.component";
import { BrandCampaignPostsCalendarComponent } from "./brand-campaign-posts-calendar/brand-campaign-posts-calendar.component";
import { BrandCampaignPostsFiltersComponent } from "./brand-campaign-posts/brand-campaign-posts-filters/brand-campaign-posts-filters.component";
import { BrandCampaignPostsListItemComponent } from "./brand-campaign-posts/brand-campaign-posts-list/brand-campaign-posts-list-item/brand-campaign-posts-list-item.component";
import { BrandCampaignPostsListComponent } from "./brand-campaign-posts/brand-campaign-posts-list/brand-campaign-posts-list.component";
import { BrandCampaignPostsComponent } from "./brand-campaign-posts/brand-campaign-posts.component";
import { BrandCampaignFacebookTargetingPickerComponent } from "./brand-campaign-promo-ads/brand-campaign-facebook-targeting-picker/brand-campaign-facebook-targeting-picker.component";
import { BrandCampaignGoogleTargetingPickerComponent } from "./brand-campaign-promo-ads/brand-campaign-google-targeting-picker/brand-campaign-google-targeting-picker.component";
import { BrandCampaignPromoAdsComponent } from "./brand-campaign-promo-ads/brand-campaign-promo-ads.component";
import { BrandCampaignSummaryComponent } from "./brand-campaign-summary/brand-campaign-summary.component";
import { BrandCampaignComponent } from "./brand-campaign/brand-campaign.component";
import { BrandCampaignDialogService } from "./brand-campaign-dialog.service";
import { BrandCampaignListComponent } from "./brand-campaign-list/brand-campaign-list.component";
import { BrandCampaignsNotFoundComponent } from "./brand-campaigns-not-found/brand-campaigns-not-found.component";
import { BrandCampaignCreationDialogComponent } from "./brand-campaign-creation-dialog/brand-campaign-creation-dialog.component";
import { BrandCampaignMoveDialogComponent } from "./brand-campaign-move-dialog/brand-campaign-move-dialog.component";
import { BrandCampaignPromoAdsWhitelistDialogComponent } from "./brand-campaign-promo-ads/brand-campaign-promo-ads-whitelist-dialog/brand-campaign-promo-ads-whitelist-dialog.component";
import { BrandCampaignProviderModule } from "../../features/brand-campaign-partners/brand-campaign.provider.module";
import { FeatureFlagGuard } from "../../shared/guards/feature-flag.guard";
import { ContentCreationDialogActionsComponent } from "./campaign-content-creation/content-cretion-dialog-actions/content-creation-dialog-actions.component";
import { LinkedinCarouselAdCreationDialogComponent } from "./campaign-content-creation/linkedin/linkedin-carousel-ad-creation-dialog/linkedin-carousel-ad-creation-dialog.component";
import { LinkedinImagePostCreationDialogComponent } from "./campaign-content-creation/linkedin/linkedin-image-post-creation-dialog/linkedin-image-post-creation-dialog.component";
import { LinkedinMultiImagePostCreationDialogComponent } from "./campaign-content-creation/linkedin/linkedin-multi-image-creation-dialog/linkedin-multi-image-post-creation-dialog.component";
import { LinkedinVideoPostCreationDialogComponent } from "./campaign-content-creation/linkedin/linkedin-video-post-creation-dialog/linkedin-video-post-creation-dialog.component";
import { BrandCalendarPostService } from "./shared/services/brand-calendar-post.service";
import { BrandCampaignInsightService } from "./shared/services/brand-campaign-insight.service";
import { BrandCampaignPartnersService } from "./shared/services/brand-campaign-partners.service";
import { BrandCampaignPostLogService } from "./shared/services/brand-campaign-post-log.service";
import { BrandCampaignPostService } from "./shared/services/brand-campaign-post.service";
import { BrandCampaignService } from "./shared/services/brand-campaign.service";
import { BrandEmailIntegrationService } from "./shared/services/brand-email-integration.service";
import { FacebookTargetingService } from "./shared/services/facebook-targeting.service";
import { GoogleTargetingService } from "./shared/services/google-targeting.service";
import { BudgetPlanComponent } from "../../shared/components/budget-plan/budget-plan.component";
import { BrandCampaignItemComponent } from "./brand-campaign-list/brand-campaign-item/brand-campaign-item.component";
import { CampaignDashboardStatsSectionComponent } from "../../shared/components/campaign-dashboard-stats-section/campaign-dashboard-stats-section.component";
import { CampaignDashboardDailyChartsComponent } from "../../shared/components/campaign-dashboard-daily-charts/campaign-dashboard-daily-charts.component";
import { TooltipComponent } from "../../shared/components/tooltip/tooltip.component";
import { CampaignPostStatsComponent } from "../../shared/components/campaign-post-stats/campaign-post-stats.component";
import { ReloadComponent } from "../../shared/components/reload/reload.component";
import { PanoramicViewerComponent } from "../../shared/components/panoramic-viewer/panoramic-viewer.component";
import { BrandCampaignInvitePartnersService } from "./brand-campaign-invite-partners/brand-campaign-invite-partners.service";

export const routes: Routes = [
  {
    path: "",
    component: BrandCampaignListComponent,
  },
  {
    path: "not-found",
    component: BrandCampaignsNotFoundComponent,
  },
  {
    path: "view/:campaignId",
    redirectTo: ":campaignId",
  },
  {
    path: ":campaignId",
    canActivate: [BrandCampaignResolverGuard()],
    component: BrandCampaignNavigationComponent,
    children: [
      { path: "", redirectTo: "dashboard", pathMatch: "full" },
      {
        path: "information",
        redirectTo: "information/settings",
        pathMatch: "full",
      },
      {
        path: "",
        component: BrandCampaignComponent,
        canActivate: [BetaCampaignGuard()],
        children: [
          {
            path: "dashboard",
            component: BrandCampaignDashboardComponent,
          },
          {
            path: "partners",
            component: BrandCampaignPartnersComponent,
          },
          {
            path: "map",
            component: BrandCampaignMapComponent,
          },
          {
            path: "posts",
            component: BrandCampaignPostsComponent,
          },
          {
            path: "posts-calendar",
            component: BrandCampaignPostsCalendarComponent,
          },
          {
            path: "information",
            loadChildren: () =>
              import(
                "./brand-campaign-information/brand-campaign-information.module"
              ).then((m) => m.BrandCampaignInformationModule),
          },
          {
            path: "content",
            component: BrandCampaignContentComponent,
          },
          {
            path: "publications",
            component: BrandContentComponent,
          },
          {
            path: "landing-page",
            component: BrandCampaignLandingPageComponent,
          },
          {
            path: "promoted-ads",
            component: BrandCampaignPromoAdsComponent,
          },
          {
            path: "content-calendar",
            component: BrandCampaignContentCalendarComponent,
          },
          {
            path: "invite-partners",
            canMatch: [FeatureFlagGuard("new-prospect-partners", true)],
            loadChildren: () =>
              import(
                "./brand-campaign-invite-partners/brand-campaign-invite-partners.module"
              ).then((m) => m.BrandCampaignInvitePartnersModule),
          },
          {
            path: "invite-partners",
            canMatch: [FeatureFlagGuard("new-prospect-partners", false)],
            loadChildren: () =>
              import(
                "./brand-campaign-invite-partners-legacy/brand-campaign-invite-partners-legacy.module"
              ).then((m) => m.BrandCampaignInvitePartnersLegacyModule),
          },
          {
            path: "pos",
            component: BrandCampaignPosComponent,
          },
          {
            path: "summary",
            component: BrandCampaignSummaryComponent,
          },
        ],
      },
    ],
  },
  // Without Campaign sidebar
  {
    path: ":campaignId",
    canActivate: [BrandCampaignResolverGuard()],
    children: [
      {
        path: "publications/templates",
        canActivate: [BetaCampaignGuard()],
        loadChildren: () =>
          import("./publication-template/publication-template.module").then(
            (m) => m.PublicationTemplateModule,
          ),
      },
    ],
  },
];

@NgModule({
  declarations: [
    CampaignCategoryFormDialogComponent,
    BrandCampaignCreationDialogComponent,
    BrandCampaignMoveDialogComponent,
    BrandCampaignListComponent,
    BrandCampaignItemComponent,
    BrandCampaignPromoAdsWhitelistDialogComponent,
    BrandCampaignsNotFoundComponent,
    BrandCampaignNavigationComponent,
    BrandCampaignContentCalendarComponent,
    BrandCampaignContentCalendarPostEditDialogComponent,
    BrandCampaignContentCalendarPostSelectionDialogComponent,
    BrandCampaignDashboardComponent,
    BrandCampaignPartnersComponent,
    BrandCampaignMapComponent,
    BrandCampaignHeaderComponent,
    BrandCampaignSubmitCheckDialogComponent,
    CampaignContentPostComponent,
    CampaignContentPostCommentsDialogComponent,
    CampaignContentFormPostImageDialogComponent,
    CampaignContentFormPostCarouselDialogComponent,
    CampaignContentFormPostVideoDialogComponent,
    CampaignContentFormBannerDialogComponent,
    CampaignContentFormLinkPostDialogComponent,
    CampaignContentFormVideoDialogComponent,
    CampaignContentFormGoogleAdDialogComponent,
    CarouselEditComponent,
    CarouselEditItemDirective,
    CarouselEditItemElementDirective,
    CarouselPreviewComponent,
    CampaignAddContentDialogComponent,
    BrandCampaignPromoAdsComponent,
    BrandCampaignNameEditDialogComponent,
    BrandCampaignPosComponent,
    BrandCampaignPartnerStatusChartDialogComponent,
    BrandCampaignContentCalendarPostsCarouselComponent,
    BrandCampaignContentCalendarPostsCarouselItemDirective,
    BrandCampaignContentCalendarPostsCarouselItemElementDirective,
    BrandCampaignPostsComponent,
    BrandCampaignSummaryComponent,
    BrandCampaignPostsCalendarComponent,
    BrandCampaignGoogleTargetingPickerComponent,
    BrandCampaignFacebookTargetingPickerComponent,
    BrandCampaignPostsFiltersComponent,
    BrandCampaignPostsListComponent,
    BrandCampaignPostsListItemComponent,
    BrandCampaignCategoryMoveDialogComponent,
    BrandCampaignLandingPageComponent,
    BrandCampaignAdDialogComponent,
    BrandCampaignContentComponent,
    BrandCampaignPartnerEditDialogComponent,
    BrandCampaignContentFormPostSuggestedTextComponent,
    BrandCampaignComponent,
    BrandCampaignContentFormFbLandingPageUrlComponent,
    ContentCreationDialogActionsComponent,
    LinkedinImagePostCreationDialogComponent,
    LinkedinVideoPostCreationDialogComponent,
    LinkedinMultiImagePostCreationDialogComponent,
    LinkedinCarouselAdCreationDialogComponent,
  ],
  imports: [
    BrandCampaignProviderModule,
    LandingPageProviderModule,
    FormsModule,
    RouterModule.forChild(routes),
    TranslateModule,
    AngularMaterialModule,
    NgCommonModule,
    SharedComponentsModule,
    SharedPipesModule,
    GoogleMapsModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    CdkTableModule,
    NgxUploaderModule,
    ReactiveFormsModule,
    MatTreeModule,
    LoaderComponent,
    NgOptimizedImage,
    PublicationProviderModule,
    BudgetPlanComponent,
    CampaignDashboardDailyChartsComponent,
    CampaignDashboardStatsSectionComponent,
    CampaignPostStatsComponent,
    TooltipComponent,
    ReloadComponent,
    PanoramicViewerComponent,
    BrandPublicationsHeaderComponent,
    DoughnutChartWithLegendComponent,
  ],
  providers: [
    BrandCampaignDialogService,
    BrandCampaignInvitePartnersService,
    BrandCalendarPostService,
    BrandCampaignService,
    BrandCampaignInsightService,
    BrandCampaignPartnersService,
    BrandCampaignPostService,
    BrandCampaignPostLogService,
    BrandEmailIntegrationService,
    BrandPartnerBudgetService,
    GoogleTargetingService,
    FacebookTargetingService,
    SelectorOptionsService,
    PublicationService,
    {
      provide: INTERVAL_RUNNER_FACTORY,
      useFactory: () => createDefaultIntervalRunner,
    },
  ],
  exports: [BrandCampaignContentFormFbLandingPageUrlComponent],
})
export class BrandCampaignModule {}
